"use client";

import * as React from "react";

import { cn } from "@baluu/ui/lib/utils";
import {
  Clock,
  MapPin,
  Users,
  Globe,
  CalendarDays,
  Check,
} from "lucide-react";
import { Button } from "@baluu/ui/components/button";
import { shouldShowTimezoneLabel } from "./utils/format";

type TermDate = {
  startDateTime: string;
  duration?: number;
};

type SessionCardProps = {
  title: string;
  subtitle?: string;
  // Optional custom session title (shown below main title)
  sessionTitle?: string;
  // Optional per-session content shown on the card
  sessionDescription?: string;
  sessionImage?: string; // resolved image URL
  sessionImageAlt?: string;

  date?: string;
  time?: string;
  timezoneLabel?: string; // Separate timezone label for stacked display
  timeWithTimezone?: string;
  location?: string;
  price?: string | number;
  spotsLeft?: number;
  hideDisplayAvailableTickets?: boolean;
  isTerm?: boolean;
  termType?: "fullTerm" | "proRata"; // For term sessions: full term or joining mid-term
  termDates?: TermDate[];
  timezone?: string;
  isOnline?: boolean; // Whether the session is online (affects timezone display)
  selected?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  className?: string;
  children?: React.ReactNode;
  // Tutor info
  tutorName?: string;
  tutorImage?: string;
  // Waitlist
  showWaitlist?: boolean;
  onJoinWaitlist?: () => void;
};

/**
 * Format date in the appropriate timezone based on session type:
 * - OFFLINE sessions: Use the course/session timezone
 * - ONLINE sessions: Use the user's local timezone
 */
function formatTermDate(
  dateString: string,
  timezone?: string,
  isOnline?: boolean,
): string {
  // Determine which timezone to use
  const displayTimezone =
    isOnline && typeof window !== "undefined"
      ? Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone || timezone
      : timezone;

  const options: Intl.DateTimeFormatOptions = {
    weekday: "short",
    day: "numeric",
    month: "long",
    timeZone: displayTimezone,
  };

  try {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("en-GB", options).format(date);
  } catch {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      weekday: "short",
      day: "numeric",
      month: "long",
    });
  }
}

/**
 * Format time in the appropriate timezone based on session type:
 * - OFFLINE sessions: Use the course/session timezone
 * - ONLINE sessions: Use the user's local timezone
 */
function formatTermTime(
  dateString: string,
  duration?: number,
  timezone?: string,
  isOnline?: boolean,
): string {
  // Determine which timezone to use
  const displayTimezone =
    isOnline && typeof window !== "undefined"
      ? Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone || timezone
      : timezone;

  const options: Intl.DateTimeFormatOptions = {
    hour: "numeric",
    minute: "2-digit",
    timeZone: displayTimezone,
  };

  try {
    const date = new Date(dateString);
    const startTime = new Intl.DateTimeFormat("en-GB", options).format(date);

    if (duration) {
      const endDate = new Date(date.getTime() + duration * 60000);
      const endTime = new Intl.DateTimeFormat("en-GB", options).format(endDate);
      const tz =
        !isOnline && shouldShowTimezoneLabel(timezone, dateString)
          ? ` (${getReadableTimezone(displayTimezone)})`
          : "";
      return `${startTime} - ${endTime}${tz}`;
    }

    return startTime;
  } catch {
    const date = new Date(dateString);
    const startTime = date.toLocaleTimeString("en-GB", {
      hour: "numeric",
      minute: "2-digit",
    });

    if (duration) {
      const endDate = new Date(date.getTime() + duration * 60000);
      const endTime = endDate.toLocaleTimeString("en-GB", {
        hour: "numeric",
        minute: "2-digit",
      });
      // No timezone suffix here: these fallback times are on the device's own
      // clock (toLocaleTimeString), so naming the course zone would stamp the
      // wrong zone onto them — and this branch fires precisely when the
      // stored zone is one Intl doesn't recognise.
      return `${startTime} - ${endTime}`;
    }

    return startTime;
  }
}

/**
 * Get a readable timezone label. Returns the full IANA identifier as-is
 * (e.g., "Europe/London", "America/New_York") for clarity.
 */
function getReadableTimezone(timezone?: string): string {
  if (!timezone) return "";
  return timezone;
}

/**
 * Format a date range string from term dates (e.g., "14 Feb – 11 Apr 2026")
 */
function formatTermDateRange(
  termDates: TermDate[],
  timezone?: string,
  isOnline?: boolean,
): string | null {
  if (termDates.length < 2) return null;

  const displayTimezone =
    isOnline && typeof window !== "undefined"
      ? Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone || timezone
      : timezone;

  const sorted = [...termDates].sort(
    (a, b) =>
      new Date(a.startDateTime).getTime() - new Date(b.startDateTime).getTime(),
  );

  const first = new Date(sorted[0].startDateTime);
  const last = new Date(sorted[sorted.length - 1].startDateTime);

  try {
    const firstYear = parseInt(
      new Intl.DateTimeFormat("en-GB", {
        year: "numeric",
        timeZone: displayTimezone,
      }).format(first),
      10,
    );
    const lastYear = parseInt(
      new Intl.DateTimeFormat("en-GB", {
        year: "numeric",
        timeZone: displayTimezone,
      }).format(last),
      10,
    );
    const sameYear = firstYear === lastYear;

    const baseOptions: Intl.DateTimeFormatOptions = {
      day: "numeric",
      month: "short",
      timeZone: displayTimezone,
    };

    const firstFormatted = new Intl.DateTimeFormat(
      "en-GB",
      sameYear ? baseOptions : { ...baseOptions, year: "numeric" },
    ).format(first);
    const lastFormatted = new Intl.DateTimeFormat("en-GB", {
      ...baseOptions,
      year: "numeric",
    }).format(last);

    return `${firstFormatted} – ${lastFormatted}`;
  } catch {
    return null;
  }
}

function SessionCard({
  title,
  subtitle,
  sessionTitle,
  sessionDescription,
  sessionImage,
  sessionImageAlt,
  date,
  time,
  timezoneLabel,
  timeWithTimezone,
  location,
  price,
  spotsLeft,
  hideDisplayAvailableTickets = false,
  isTerm = false,
  termType,
  termDates = [],
  timezone,
  isOnline = false,
  selected,
  disabled,
  onClick,
  className,
  children,
  tutorName,
  tutorImage,
  showWaitlist,
  onJoinWaitlist,
}: SessionCardProps) {
  const [showAllDates, setShowAllDates] = React.useState(false);

  const handleCardClick = () => {
    if (!disabled && onClick) {
      onClick();
    }
  };

  // Format spots text - respects hideDisplayAvailableTickets setting
  // Use <= 0 to handle both sold out (0) and overbooked (negative) cases
  const spotsText =
    spotsLeft !== undefined
      ? spotsLeft <= 0
        ? "Sold out"
        : spotsLeft <= 3
          ? hideDisplayAvailableTickets ? "Few spots left" : `${spotsLeft} spots left`
          : "Spots available"
      : null;

  // Display time with timezone if provided, otherwise use time
  const displayTime = timeWithTimezone || time;

  // Term-specific computed data
  const isTermCard = isTerm && termDates.length > 0;
  const sortedTermDates = React.useMemo(() => {
    if (!isTermCard) return [];
    return [...termDates].sort(
      (a, b) =>
        new Date(a.startDateTime).getTime() - new Date(b.startDateTime).getTime(),
    );
  }, [isTermCard, termDates]);
  const termDateRange = isTermCard
    ? formatTermDateRange(termDates, timezone, isOnline)
    : null;
  const termSessionCount = isTermCard ? termDates.length : 0;

  // Selected indicator — a filled check that *appears* on selection. Because it
  // renders a distinct new element (not just a border-colour swap), selection
  // stays obvious even when a school customises `--primary` to a low-contrast
  // brand colour that sits close to the card background.
  const selectedCheck = selected ? (
    <span
      aria-hidden="true"
      className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-sm ring-1 ring-inset ring-black/10"
    >
      <Check className="h-3.5 w-3.5" strokeWidth={3} />
    </span>
  ) : null;

  return (
    <div
      // The page's stroke and card shadow find it by this (webcomponents globals.css).
      data-slot="session-card"
      role={onClick ? "button" : undefined}
      tabIndex={onClick && !disabled ? 0 : undefined}
      onClick={handleCardClick}
      onKeyDown={
        onClick && !disabled
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleCardClick();
              }
            }
          : undefined
      }
      className={cn(
        "relative rounded-lg border-2 p-4 transition-all",
        selected ? "bg-primary/5" : "bg-card",
        onClick &&
          !disabled &&
          "cursor-pointer hover:border-primary/50 hover:shadow-sm",
        selected && "border-primary shadow-sm",
        !selected && "border-border",
        // Apply opacity to whole card when disabled without waitlist
        disabled && !showWaitlist && "opacity-50 cursor-not-allowed",
        // For sold out with waitlist: cursor-not-allowed but no opacity on container
        disabled && showWaitlist && "cursor-not-allowed",
        className,
      )}
    >
      {/* Content wrapper - fades when sold out with waitlist option */}
      <div className={cn(disabled && showWaitlist && "opacity-50")}>
        {/* Desktop: Single row layout | Mobile: Stacked layout */}
        {/* Desktop Layout - hidden on mobile */}
        {/* Term cards use a two-row layout so the title can wrap freely */}
        {isTermCard ? (
          <div className="hidden sm:block">
            {/* Row 1: Title + badge */}
            <div className="flex items-start justify-between gap-4">
              {sessionImage && (
                <img
                  src={sessionImage}
                  alt={sessionImageAlt ?? ""}
                  className="w-16 h-16 rounded-lg object-cover shrink-0"
                />
              )}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <h4 className="font-semibold text-foreground text-lg leading-tight">
                    {title}
                  </h4>
                  {selectedCheck}
                </div>
                {sessionTitle && (
                  <span className="text-muted-foreground block">
                    {sessionTitle}
                  </span>
                )}
                {sessionDescription && (
                  <p className="text-sm text-muted-foreground line-clamp-2 mt-0.5">
                    {sessionDescription}
                  </p>
                )}
                {(isTerm || subtitle || termType) && (
                  <span className={cn(
                    "font-medium mt-1 inline-flex items-center px-2 py-0.5 rounded-full text-sm",
                    termType === "proRata" ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" : "bg-muted text-muted-foreground"
                  )}>
                    {subtitle || (termType === "proRata" ? "Pro rata" : "Full term")}
                  </span>
                )}
              </div>
              {/* Price - aligned to end */}
              {price !== undefined && price !== null && price !== "" && (
                <span className="font-bold text-foreground text-lg shrink-0">
                  {price}
                </span>
              )}
            </div>
            {/* Row 2: Metadata — date range, location, availability on a single line */}
            <div className="flex items-center gap-4 mt-2 flex-nowrap overflow-hidden">
              {termDateRange && (
                <div className="flex items-center gap-2 shrink-0">
                  <CalendarDays className="w-4 h-4 text-muted-foreground shrink-0" />
                  <span className="text-foreground whitespace-nowrap">{termDateRange}</span>
                  {termSessionCount > 0 && (
                    <span className="text-muted-foreground whitespace-nowrap">· {termSessionCount} sessions</span>
                  )}
                </div>
              )}
              {location && (
                <div className="flex items-center gap-2 shrink-0">
                  {isOnline ? (
                    <Globe className="w-4 h-4 text-muted-foreground shrink-0" />
                  ) : (
                    <MapPin className="w-4 h-4 text-muted-foreground shrink-0" />
                  )}
                  <span className="text-foreground whitespace-nowrap">
                    {location}
                  </span>
                </div>
              )}
              {spotsText && (
                <div
                  className={cn(
                    "flex items-center gap-1.5 px-2.5 py-0.5 rounded-full shrink-0",
                    spotsLeft !== undefined && spotsLeft <= 3 && spotsLeft > 0
                      ? "bg-amber-100 dark:bg-amber-900/30"
                      : spotsLeft !== undefined && spotsLeft <= 0
                        ? "bg-red-100 dark:bg-red-900/30"
                        : "bg-card-hover",
                  )}
                >
                  <Users
                    className={cn(
                      "w-4 h-4 shrink-0",
                      spotsLeft !== undefined && spotsLeft <= 3 && spotsLeft > 0
                        ? "text-amber-700 dark:text-amber-400"
                        : spotsLeft !== undefined && spotsLeft <= 0
                          ? "text-red-600 dark:text-red-400"
                          : "text-muted-foreground",
                    )}
                  />
                  <span
                    className={cn(
                      "font-medium whitespace-nowrap",
                      spotsLeft !== undefined && spotsLeft <= 3 && spotsLeft > 0
                        ? "text-amber-700 dark:text-amber-400"
                        : spotsLeft !== undefined && spotsLeft <= 0
                          ? "text-red-600 dark:text-red-400"
                          : "text-muted-foreground",
                    )}
                  >
                    {spotsText}
                  </span>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="hidden sm:flex items-center gap-5 flex-wrap">
          {/* Per-session photo - e.g. the painting for this date */}
          {sessionImage && (
            <img
              src={sessionImage}
              alt={sessionImageAlt ?? ""}
              className="w-16 h-16 rounded-lg object-cover shrink-0"
            />
          )}
          {/* Title. With a description the column must be able to grow AND be
              bounded, or shrink-0 sizes it to the description's max-content
              width and line-clamp never engages. */}
          <div
            className={cn(
              "min-w-0",
              sessionDescription ? "flex-1 basis-64" : "shrink-0",
            )}
          >
            <div className="flex items-center gap-2">
              <h4 className="font-semibold text-foreground text-lg leading-tight">
                {title}
              </h4>
              {selectedCheck}
            </div>
            {/* Session title - optional custom title for this session */}
            {sessionTitle && (
              <span className="text-muted-foreground block">
                {sessionTitle}
              </span>
            )}
            {sessionDescription && (
              <p className="text-sm text-muted-foreground line-clamp-2 mt-0.5">
                {sessionDescription}
              </p>
            )}
            {(isTerm || subtitle || termType) && (
              <span className={cn(
                "font-medium inline-flex items-center px-2 py-0.5 rounded-full",
                termType === "proRata" ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" : "bg-muted text-muted-foreground"
              )}>
                {subtitle || (termType === "proRata" ? "Pro rata" : "Full term")}
              </span>
            )}
          </div>

          {/* Divider */}
          <div className="h-6 w-px bg-border hidden sm:block" />

          {/* Time with icon */}
          {displayTime && (
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-muted-foreground shrink-0" />
              <span className="text-base text-foreground">{displayTime}</span>
              {timezoneLabel && (
                <span className="text-muted-foreground">
                  ({timezoneLabel})
                </span>
              )}
            </div>
          )}

          {/* Location / Online indicator (hidden when no location resolves) */}
          {location && (
            <div className="flex items-center gap-2">
              {isOnline ? (
                <Globe className="w-5 h-5 text-muted-foreground shrink-0" />
              ) : (
                <MapPin className="w-5 h-5 text-muted-foreground shrink-0" />
              )}
              <span className="text-base text-foreground">{location}</span>
            </div>
          )}

          {/* Availability with icon */}
          {spotsText && (
            <div
              className={cn(
                "flex items-center gap-2 px-2.5 py-1 rounded-full",
                spotsLeft !== undefined && spotsLeft <= 3 && spotsLeft > 0
                  ? "bg-amber-100 dark:bg-amber-900/30"
                  : spotsLeft !== undefined && spotsLeft <= 0
                    ? "bg-red-100 dark:bg-red-900/30"
                    : "bg-card-hover",
              )}
            >
              <Users
                className={cn(
                  "w-4 h-4 shrink-0",
                  spotsLeft !== undefined && spotsLeft <= 3 && spotsLeft > 0
                    ? "text-amber-700 dark:text-amber-400"
                    : spotsLeft !== undefined && spotsLeft <= 0
                      ? "text-red-600 dark:text-red-400"
                      : "text-muted-foreground",
                )}
              />
              <span
                className={cn(
                  "font-medium",
                  spotsLeft !== undefined && spotsLeft <= 3 && spotsLeft > 0
                    ? "text-amber-700 dark:text-amber-400"
                    : spotsLeft !== undefined && spotsLeft <= 0
                      ? "text-red-600 dark:text-red-400"
                      : "text-muted-foreground",
                )}
              >
                {spotsText}
              </span>
            </div>
          )}

          {/* Price - pushed to end */}
          {price !== undefined && price !== null && price !== "" && (
            <div className="flex items-center gap-2 ml-auto shrink-0">
              <span className="font-bold text-foreground text-lg">
                {price}
              </span>
            </div>
          )}
        </div>
        )}

      {/* Mobile Layout - hidden on desktop */}
      <div className="sm:hidden">
        {/* Header: Title and Price */}
        <div className="flex items-start justify-between gap-3">
          {sessionImage && (
            <img
              src={sessionImage}
              alt={sessionImageAlt ?? ""}
              className="w-14 h-14 rounded-lg object-cover shrink-0"
            />
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h4 className="font-semibold text-foreground text-base leading-tight">
                {title}
              </h4>
              {selectedCheck}
            </div>
            {/* Session title - optional custom title for this session */}
            {sessionTitle && (
              <span className="text-muted-foreground block">
                {sessionTitle}
              </span>
            )}
            {sessionDescription && (
              <p className="text-sm text-muted-foreground line-clamp-2 mt-0.5">
                {sessionDescription}
              </p>
            )}
            {(isTerm || subtitle || termType) && (
              <span className={cn(
                "font-medium mt-0.5 inline-flex items-center px-2 py-0.5 rounded-full",
                termType === "proRata" ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" : "bg-muted text-muted-foreground"
              )}>
                {subtitle || (termType === "proRata" ? "Pro rata" : "Full term")}
              </span>
            )}
          </div>
          {price !== undefined && price !== null && price !== "" && (
            <span className="font-bold text-foreground text-lg shrink-0">
              {price}
            </span>
          )}
        </div>

        {/* Info Grid: Time, Location, Availability */}
        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2">
          {/* Time - hidden for terms since time is shown per session */}
          {displayTime && !isTermCard && (
            <div className="flex items-center gap-1.5">
              <Clock className="w-4 h-4 text-muted-foreground shrink-0" />
              <div className="flex items-baseline gap-1">
                <span className="text-foreground">
                  {displayTime}
                </span>
                {timezoneLabel && (
                  <span className="text-muted-foreground">
                    ({timezoneLabel})
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Date range and session count for terms */}
          {isTermCard && termDateRange && (
            <div className="flex items-center gap-1.5">
              <CalendarDays className="w-4 h-4 text-muted-foreground shrink-0" />
              <span className="text-foreground">{termDateRange}</span>
              {termSessionCount > 0 && (
                <span className="text-muted-foreground">· {termSessionCount} sessions</span>
              )}
            </div>
          )}

          {/* Location / Online indicator (hidden when no location resolves) */}
          {location && (
            <div className="flex items-center gap-1.5">
              {isOnline ? (
                <Globe className="w-4 h-4 text-muted-foreground shrink-0" />
              ) : (
                <MapPin className="w-4 h-4 text-muted-foreground shrink-0" />
              )}
              <span className="text-foreground">{location}</span>
            </div>
          )}

          {spotsText && (
            <div
              className={cn(
                "flex items-center gap-1.5 px-2 py-0.5 rounded-full",
                spotsLeft !== undefined && spotsLeft <= 3 && spotsLeft > 0
                  ? "bg-amber-100 dark:bg-amber-900/30"
                  : spotsLeft !== undefined && spotsLeft <= 0
                    ? "bg-red-100 dark:bg-red-900/30"
                    : "bg-card-hover",
              )}
            >
              <Users
                className={cn(
                  "w-3.5 h-3.5 shrink-0",
                  spotsLeft !== undefined && spotsLeft <= 3 && spotsLeft > 0
                    ? "text-amber-700 dark:text-amber-400"
                    : spotsLeft !== undefined && spotsLeft <= 0
                      ? "text-red-600 dark:text-red-400"
                      : "text-muted-foreground",
                )}
              />
              <span
                className={cn(
                  "font-medium",
                  spotsLeft !== undefined && spotsLeft <= 3 && spotsLeft > 0
                    ? "text-amber-700 dark:text-amber-400"
                    : spotsLeft !== undefined && spotsLeft <= 0
                      ? "text-red-600 dark:text-red-400"
                      : "text-muted-foreground",
                )}
              >
                {spotsText}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Session dates for term cards */}
      {isTermCard && sortedTermDates.length > 0 && (
        <div className="mt-3 pt-3 border-t border-border/50">
          <div className="space-y-1">
            {(showAllDates ? sortedTermDates : sortedTermDates.slice(0, 3)).map((td, i) => (
              <div
                key={i}
                className="flex items-center justify-between py-1.5 px-2 rounded text-secondary"
              >
                <span className="font-medium">
                  {formatTermDate(td.startDateTime, timezone, isOnline)}
                </span>
                <span className="text-muted-foreground">
                  {formatTermTime(td.startDateTime, td.duration, timezone, isOnline)}
                </span>
              </div>
            ))}
          </div>
          {sortedTermDates.length > 3 && (
            <div className="mt-1.5">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowAllDates(!showAllDates);
                }}
                className="text-muted-foreground hover:text-foreground p-0 h-auto font-medium"
              >
                {showAllDates
                  ? "Show less"
                  : `+ ${sortedTermDates.length - 3} more sessions`}
              </Button>
            </div>
          )}
        </div>
      )}
      </div>

      {/* Footer: Tutor and Waitlist - stays at full opacity */}
      {(tutorName || (showWaitlist && onJoinWaitlist)) && (
        <div className="mt-3 pt-3 border-t border-border/50 flex items-center justify-between gap-3">
          {/* Tutor info */}
          {tutorName && (
            <div className="flex items-center gap-2">
              {tutorImage ? (
                <img
                  src={tutorImage}
                  alt={tutorName}
                  className="w-8 h-8 rounded-full object-cover border border-border"
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center font-medium text-primary">
                  {tutorName
                    .split(" ")
                    .map((n) => n[0])
                    .join("")
                    .slice(0, 2)
                    .toUpperCase()}
                </div>
              )}
              <div className="flex flex-col">
                <span className="text-muted-foreground">
                  Instructor
                </span>
                <span className="font-medium text-foreground">
                  {tutorName}
                </span>
              </div>
            </div>
          )}

          {/* Waitlist button for sold out */}
          {showWaitlist && onJoinWaitlist && (
            <Button
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onJoinWaitlist();
              }}
              className="bg-secondary text-secondary-foreground font-medium hover:bg-secondary/80 shrink-0 transition-colors"
            >
              Join Waiting List
            </Button>
          )}
        </div>
      )}

      {/* Date if provided separately */}
      {date && <p className="text-muted-foreground mt-2">{date}</p>}

      {children && (
        <div className="mt-4 pt-4 border-t border-border/50">
          {children}
        </div>
      )}
    </div>
  );
}

type SessionCardGroupProps = {
  title?: string;
  children: React.ReactNode;
  className?: string;
};

function SessionCardGroup({
  title,
  children,
  className,
}: SessionCardGroupProps) {
  return (
    <div className={cn("space-y-4", className)}>
      {title && (
        <h3 className="font-bold text-lg text-foreground uppercase tracking-wide">
          {title}
        </h3>
      )}
      <div className="space-y-3">{children}</div>
    </div>
  );
}

SessionCard.Group = SessionCardGroup;

export { SessionCard };
